<template>
    <div class="freight-page">
        <div class="content-wrapper">
            <div class="page-header">
                <div class="page-title"><span>运费模板</span></div>
                <div class="page-search">
                    <div class="search-input-box">
                        <input v-model="keyword" type="text" placeholder="输入模板名称" class="search-input-native"
                            @keyup.enter="onSearch" />
                        <img src="@/assets/images/icon-search.png" class="search-icon" @click="onSearch" />
                    </div>
                </div>
            </div>

            <div class="toolbar">
                <Button class="add-btn" type="primary" icon="md-add" @click="onAdd">新建运费模板</Button>
                <div class="batch-box">
                    <span class="selected-text">已选{{ selectedIds.length }}条</span>
                    <Button class="batch-delete-btn" :disabled="selectedIds.length === 0"
                        @click="onBatchDelete">批量删除</Button>
                </div>
            </div>

            <div class="div-table">
                <div class="div-thead">
                    <div class="th" style="grid-column: 1">序号</div>
                    <div class="th" style="grid-column: 2">模板名称</div>
                    <div class="th" style="grid-column: 3">发货地</div>
                    <div class="th" style="grid-column: 4">是否包邮</div>
                    <div class="th" style="grid-column: 5">收货地</div>
                    <div class="th" style="grid-column: 6">运费规则</div>
                    <div class="th" style="grid-column: 7">备注</div>
                    <div class="th" style="grid-column: 8">默认模板</div>
                    <div class="th" style="grid-column: 9">操作</div>
                </div>

                <div class="div-tbody" v-if="templateGroups.length">
                    <div v-for="(g, gi) in templateGroups" :key="gi" class="tpl-group">
                        <div v-for="(row, ri) in g.cells" :key="ri" class="rule-cells">
                            <div v-if="row.isFirst" class="td" :style="spanStyle(1, row)">
                                <Checkbox :value="selectedIds.includes(row.template.id)"
                                    @on-change="(val) => toggleSelect(val, row.template.id)"></Checkbox>
                                <span class="idx-text">{{ row.index }}</span>
                            </div>
                            <div v-if="row.isFirst" class="td" :style="spanStyle(2, row)">{{ row.name }}</div>
                            <div v-if="row.isFirst" class="td" :style="spanStyle(3, row)">{{ row.from_address }}</div>
                            <div v-if="row.isFirst" class="td" :style="spanStyle(4, row)">{{ row.is_all_free_text }}
                            </div>

                            <div class="td" :style="cellStyle(5, row)">{{ row.province_text }}</div>
                            <div class="td" :style="cellStyle(6, row)">
                                <template v-if="row.ruleData.free">包邮</template>
                                <template v-else>
                                    <span>首件{{ row.ruleData.firstQty }}件内</span><span class="price">{{ row.ruleData.firstPrice }}</span><span>元；每增加{{ row.ruleData.moreQty }}件，增加运费</span><span class="price">{{ row.ruleData.morePrice }}</span><span>元</span>
                                </template>
                            </div>

                            <div v-if="row.isFirst" class="td" :style="spanStyle(7, row)">{{ row.memo }}</div>
                            <div v-if="row.isFirst" class="td" :style="spanStyle(8, row)">
                                <div v-if="row.template.is_default" class="default-tag">默认模板</div>
                                <span v-else class="set-default-link" @click="onSetDefault(row.template)">设为默认模板</span>
                            </div>
                            <div v-if="row.isFirst" class="td last-col" :style="spanStyle(9, row)">
                                <span class="action-link" @click="onEdit(row.template)">编辑</span>
                                <span class="action-link delete" @click="onDelete(row.template.id)">删除</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="div-empty">暂无数据</div>
            </div>
        </div>


        <Edit :visible.sync="showEdit" :edit-data="currentRow" @success="loadList" />
    </div>
</template>

<script>
import {
    ShopFreightTemplateList,
    ShopFreightTemplateDelete,
    ShopFreightTemplateSetDefault
} from "@/api";
import Edit from "./components/Edit.vue";

export default {
    name: "Freight",
    components: { Edit },
    data() {
        return {
            loading: false,
            keyword: "",
            list: [],
            selectedIds: [],
            showEdit: false,
            currentRow: null
        };
    },
    computed: {
        templateGroups() {
            const groups = [];
            let gridRow = 1;
            this.list.forEach((template, index) => {
                const rules =
                    template.setting_json && template.setting_json.length
                        ? template.setting_json
                        : [null];
                const cells = rules.map((rule, rIndex) => {
                    const cell = {
                        template,
                        rule,
                        isFirst: rIndex === 0,
                        rowSpan: rules.length,
                        gridRow,
                        index: index + 1,
                        name: template.name,
                        from_address: this.formatFromAddress(template),
                        is_all_free_text: template.is_all_free ? "包邮" : "否",
                        province_text: this.formatProvince(rule),
                        ruleData: this.buildRuleData(template, rule),
                        memo: template.memo || "-",
                        update_time: this.formatTime(template.update_time)
                    };
                    gridRow++;
                    return cell;
                });
                groups.push({ template, cells });
            });
            return groups;
        }
    },
    mounted() {
        this.loadList();
    },
    methods: {
        onSearch() {
            this.loadList();
        },
        async loadList() {
            this.loading = true;
            try {
                const res = await ShopFreightTemplateList({ keyword: this.keyword });
                const { code, data } = res;
                if (code === 0) {
                    this.list = data || [];
                    this.selectedIds = [];
                } else {
                    this.$layer.msg(res.msg || "获取列表失败");
                }
            } catch (e) {
                this.$layer.msg("获取列表失败");
            } finally {
                this.loading = false;
            }
        },
        spanStyle(col, row) {
            return {
                gridColumn: col,
                gridRow: `${row.gridRow} / span ${row.rowSpan}`
            };
        },
        cellStyle(col, row) {
            return {
                gridColumn: col,
                gridRow: row.gridRow
            };
        },
        toggleSelect(checked, id) {
            if (checked) {
                if (!this.selectedIds.includes(id)) {
                    this.selectedIds.push(id);
                }
            } else {
                this.selectedIds = this.selectedIds.filter((item) => item !== id);
            }
        },
        formatFromAddress(template) {
            const parts = [template.from_province, template.from_city, template.from_zone].filter(Boolean);
            return parts.length ? parts.join("/") : "-";
        },
        formatProvince(rule) {
            if (!rule) return "全国默认";
            const list = rule.province_list || [];
            if (!list.length) return "全国默认";
            return list.map((p) => p.name).join("、");
        },
        buildRuleData(template, rule) {
            const isAllFree = template.is_all_free;
            const firstQty = rule ? rule.first_qty : template.first_qty;
            const firstPrice = rule ? rule.first_price : template.first_price;
            const moreQty = rule ? rule.more_qty : template.more_qty;
            const morePrice = rule ? rule.more_price : template.more_price;
            if ((isAllFree && !rule) || (Number(firstPrice) === 0 && Number(morePrice) === 0)) {
                return { free: true };
            }
            return {
                free: false,
                firstQty: firstQty || 1,
                firstPrice: firstPrice || 0,
                moreQty: moreQty || 1,
                morePrice: morePrice || 0
            };
        },
        formatTime(time) {
            if (!time) return "-";
            const date = new Date(time.replace(/-/g, "/"));
            if (isNaN(date.getTime())) return time;
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");
            const h = String(date.getHours()).padStart(2, "0");
            const min = String(date.getMinutes()).padStart(2, "0");
            const s = String(date.getSeconds()).padStart(2, "0");
            return `${y}-${m}-${d} ${h}:${min}:${s}`;
        },
        onAdd() {
            this.currentRow = null;
            this.showEdit = true;
        },
        onEdit(row) {
            this.currentRow = row;
            this.showEdit = true;
        },
        onDelete(id) {
            this.$DModal.confirm({
                content: "确认删除？",
                onOK: async () => {
                    try {
                        const res = await ShopFreightTemplateDelete({ ids: [id] });
                        if (res.code === 0) {
                            this.$layer.msg("删除成功");
                            this.loadList();
                        } else {
                            this.$layer.msg(res.msg || "删除失败");
                        }
                    } catch (e) {
                        this.$layer.msg("删除失败");
                    }
                }
            });
        },
        onBatchDelete() {
            if (!this.selectedIds.length) return;
            this.$DModal.confirm({
                content: "确定删除选中的运费模板吗？",
                onOK: async () => {
                    try {
                        const res = await ShopFreightTemplateDelete({ ids: this.selectedIds });
                        if (res.code === 0) {
                            this.$layer.msg("删除成功");
                            this.loadList();
                        } else {
                            this.$layer.msg(res.msg || "删除失败");
                        }
                    } catch (e) {
                        this.$layer.msg("删除失败");
                    }
                }
            });
        },
        onSetDefault(row) {
            ShopFreightTemplateSetDefault({ id: row.id })
                .then((res) => {
                    if (res.code === 0) {
                        this.$layer.msg("设置成功");
                        this.loadList();
                    } else {
                        this.$layer.msg(res.msg || "设置失败");
                    }
                })
                .catch(() => {
                    this.$layer.msg("设置失败");
                });
        }
    }
};
</script>

<style scoped lang="scss">
.freight-page {
    width: 100%;
    background: #ffffff;
    height: 100%;

    .content-wrapper {
        padding: 30px 30px 0 30px;

        .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid #d6d6d6;
        }

        .page-title {
            width: 100%;
            // padding-bottom: 15px;
            display: flex;
            align-items: center;
            column-gap: 30px;

            span {
                font-weight: bold;
                font-size: 18px;
                color: #000000;
                padding-bottom: 17px;
                border-bottom: 3px solid #ff5967;
            }

        }

        .page-search {
            .search-input-box {
                position: relative;
                width: 280px;
                height: 42px;
                background: #f3f4f6;
                border-radius: 24px 24px 24px 24px;
                display: flex;
                align-items: center;
                padding: 0 16px;

                .search-input-native {
                    flex: 1;
                    height: 100%;
                    border: none;
                    outline: none;
                    background: transparent;
                    font-size: 14px;
                    color: #333333;

                    &::placeholder {
                        color: #999999;
                    }
                }

                .search-icon {
                    width: 18px;
                    height: 18px;
                    cursor: pointer;
                    flex-shrink: 0;
                    margin-left: 8px;
                }
            }
        }
    }




    .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 20px 0 16px;

        .add-btn {
            width: 152px;
            height: 37px;
            padding: 0;
            background: #0d99ff;
            border-color: #0d99ff;
            border-radius: 4px 4px 4px 4px;

            &:hover {
                background: #33aaff;
                border-color: #33aaff;
            }
        }

        .batch-box {
            display: flex;
            align-items: center;
            column-gap: 12px;

            .selected-text {
                color: #666666;
                font-size: 14px;
            }

            .batch-delete-btn {
                width: 110px;
                height: 39px;
                padding: 0;
                border-radius: 4px 4px 4px 4px;
                border: 1px solid #ff5967;
                color: #ff5967;
                font-size: 14px;
                background: #ffffff;

                &:hover:not(:disabled) {
                    color: #ff5967;
                    border-color: #ff5967;
                    background: #fff0f1;
                }
            }
        }
    }

    .div-table {
        border: 1px solid #e3e3e3;
        border-radius: 8px;
        overflow: hidden;
        background: #ffffff;

        .div-thead,
        .div-tbody {
            display: grid;
            grid-template-columns: 90px minmax(100px, 1fr) minmax(120px, 1fr) 100px minmax(120px, 1fr) minmax(220px, 2.2fr) minmax(120px, 1.2fr) 110px 110px;
        }

        .tpl-group,
        .rule-cells {
            display: contents;
        }

        .div-thead {
            background: #f6f7fa;

            .th {
                height: 56px;
                display: flex;
                align-items: center;
                padding: 0 12px;
                font-size: 14px;
                color: #666666;
                border-right: 1px solid #e3e3e3;
                border-bottom: 1px solid #e3e3e3;

                &:last-child {
                    border-right: none;
                }
            }
        }

        .div-tbody {
            .td {
                display: flex;
                align-items: center;
                padding: 12px;
                font-size: 14px;
                color: #333333;
                border-right: 1px solid #e3e3e3;
                border-bottom: 1px solid #e3e3e3;
                word-break: break-all;

                &.last-col {
                    border-right: none;
                }

                .price {
                    color: #ff5967;
                    font-weight: 600;
                }
            }

            .idx-text {
                margin-left: 8px;
            }

            .default-tag {
                width: 78px;
                height: 35px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #ffeff0;
                border-radius: 4px 4px 4px 4px;
                border: 1px solid #ffc3c7;
                color: #ff5967;
                font-size: 14px;
            }

            .set-default-link {
                color: #0d99ff;
                cursor: pointer;
                font-size: 14px;

                &:hover {
                    color: #3fb0ff;
                }
            }

            .action-link {
                color: #0d99ff;
                cursor: pointer;
                font-size: 14px;
                margin-right: 12px;

                &:hover {
                    color: #3fb0ff;
                }

                &.delete {
                    color: #0d99ff;

                    &:hover {
                        color: #3fb0ff;
                    }
                }
            }

        }

        .div-empty {
            padding: 48px 0;
            text-align: center;
            color: #999999;
            font-size: 14px;
        }
    }
}
</style>
